import { useCallback, useEffect, useState } from "react";
import type { SlotProps } from "../../../core/platform/registry/registry";
import { useAuth } from "../../../core/platform/auth/AuthProvider";
import { getOpenShift } from "../../../core/data/local/repositories";
import { CashOutForm } from "./CashOutForm";
import { BooksManager } from "./BooksManager";

/**
 * The books' surface at the till (T020).
 *
 * One component for the whole module, because a module gets one component per
 * slot. It carries two buttons with two different audiences, and they are gated
 * separately:
 *
 * - **Cash out**, for whoever is on shift, appears only while a shift is open
 *   on this terminal: drawer money belongs to a shift, and the paid-out has to
 *   land in the one being counted.
 * - **Books**, for owners and managers, does not wait for a shift. The books
 *   are read between shifts as often as during one — most often after closing,
 *   when no shift is open at all.
 */
export function BooksActions({ terminalId, staffId, token }: SlotProps) {
  const { can } = useAuth();
  const terminal = typeof terminalId === "string" ? terminalId : null;
  const staff = typeof staffId === "string" ? staffId : null;
  const sessionToken = typeof token === "string" ? token : null;
  const canView = can("books.view");

  const [shiftId, setShiftId] = useState<string | null>(null);
  const [open, setOpen] = useState<"cash-out" | "books" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const findShift = useCallback(async () => {
    if (!terminal) return;
    setShiftId((await getOpenShift(terminal))?.id ?? null);
  }, [terminal]);

  useEffect(() => {
    void findShift();
  }, [findShift]);

  const canCashOut = terminal !== null && staff !== null && shiftId !== null;
  const canOpenBooks = canView && sessionToken !== null;

  if (!canCashOut && !canOpenBooks) return null;

  return (
    <>
      {canCashOut && (
        <button
          type="button"
          className="terminal__signout books__button"
          onClick={() => setOpen("cash-out")}
        >
          Cash out
        </button>
      )}

      {canOpenBooks && (
        <button type="button" className="terminal__signout books__button" onClick={() => setOpen("books")}>
          Books
        </button>
      )}

      {open === "cash-out" && canCashOut && (
        <div className="till__overlay">
          <CashOutForm
            shiftId={shiftId}
            terminalId={terminal}
            staffId={staff}
            onDone={(message) => {
              setOpen(null);
              setNotice(message);
              void findShift();
            }}
            onCancel={() => setOpen(null)}
          />
        </div>
      )}

      {open === "books" && sessionToken && (
        <div className="till__overlay">
          <BooksManager token={sessionToken} onClose={() => setOpen(null)} />
        </div>
      )}

      {notice && (
        <div className="books__toast" role="status">
          <span>{notice}</span>
          <button type="button" className="books__dismiss" onClick={() => setNotice(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}
    </>
  );
}

export default BooksActions;
